/**
 * Utilitaires partagés du module Conformité (Lot F) — libellés métier, formats
 * de nombres (hL 3 décimales, kg, bouteilles), téléchargement des exports, et
 * petites briques d'affichage (badge d'état, badge de sens).
 *
 * Les libellés « code technique → mot vigneron » MIROIR de
 * `apps/api/src/domain/conformite/registre-libelles.ts` (source de vérité côté
 * serveur, utilisée par les exports PDF/CSV). On duplique ici le strict minimum
 * d'affichage pour que l'écran parle exactement comme le document exporté ;
 * aucune règle métier n'est portée par ce fichier.
 */

import type {
  CategorieProduitRegistre,
  Marche,
  RegimeDroits,
  RegistreExportSortie,
  SensRegistre,
} from "@champ-erp/contracts";
import { Badge, StatusDot, type StatusVariant } from "@champ-erp/ui";
import { Lock } from "lucide-react";

import { telechargerBase64 } from "../../lib/download.js";

import { ETAT_DECLARATION_CONFIG, type EtatDeclaration } from "./echeances.js";

// ────────────────────────────────────────────────────────────────────────────
// Routes du module (référencées en <Link> ; les pages DRM/DR/DS/CRD sont câblées
// par d'autres agents — on ne connaît d'elles que leur chemin).
// ────────────────────────────────────────────────────────────────────────────

export const ROUTES_CONFORMITE = {
  hub: "/conformite",
  registre: "/conformite/registre",
  drm: "/conformite/drm",
  dr: "/conformite/dr",
  ds: "/conformite/ds",
  capsules: "/conformite/capsules",
  /** Relevé des ventes hors facture électronique (FE-5). */
  releveVentes: "/conformite/releve-ventes",
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Libellés métier (miroir registre-libelles.ts)
// ────────────────────────────────────────────────────────────────────────────

export const CATEGORIE_LABELS: Record<CategorieProduitRegistre, string> = {
  raisin: "Raisin",
  mout: "Moût",
  rebeche: "Rebêche",
  preparation: "Préparation de tirage",
  sous_produit: "Sous-produits liquides",
  vin_clair: "Vin clair",
  vin_reserve: "Vin de réserve",
  sur_lattes: "Sur lattes",
  degorgee: "Dégorgée",
  habillee: "Habillée",
};

/** Sens → libellé + variante de StatusDot (jamais la couleur seule, UX §4). */
export const SENS_CONFIG: Record<SensRegistre, { label: string; dot: StatusVariant }> = {
  entree: { label: "Entrée", dot: "ok" },
  sortie: { label: "Sortie", dot: "warn" },
  transformation: { label: "Transformation", dot: "info" },
};

export const MARCHE_LABELS: Record<Marche, string> = {
  france: "France",
  ue: "Union européenne",
  export: "Export (hors UE)",
};

export const REGIME_LABELS: Record<RegimeDroits, string> = {
  acquittes: "Droits acquittés",
  suspendus: "Droits suspendus",
};

/** Sous-type d'opération (`type_operation`) → libellé métier vigneron. */
const TYPE_OPERATION_LABELS: Record<string, string> = {
  liquide_transfert: "Transfert de liquide",
  liquide_melange: "Mélange",
  liquide_assemblage: "Assemblage",
  liquide_reserve: "Affectation de réserve",
  liquide_mise_en_reserve: "Mise en réserve",
  liquide_mouvement_reserve: "Mouvement de réserve",
  liquide_perte: "Perte de liquide",
  liquide_tirage: "Prélèvement au tirage",
  liquide_soutirage: "Soutirage",
  liquide_debourbage: "Débourbage",
  liquide_pressurage_fractions: "Jus issus du pressurage",
  liquide_vinification_maceree: "Vinification de macération",
  liquide_degorgement: "Dégorgement — liquide",
  liquide_chaptalisation: "Enrichissement",
  liquide_entree_vin_clair: "Entrée de vin clair",
  liquide_fabrication_preparation: "Fabrication de préparation",
  liquide_reception_preparation: "Réception de préparation",
  liquide_annulation_preparation: "Annulation de préparation",
  liquide_qualification_homogeneite: "Homogénéité constatée",
  pesee_vinification_propre: "Réception de vendange",
  pesee_vente_raisin: "Vente de raisin",
  pesee_vente_mout: "Vente de moût",
  livraison_cooperative: "Livraison coopérative",
  pressurage_cuvee: "Pressurage (cuvée)",
  pressurage_taille: "Pressurage (taille)",
  pressurage_rebeche: "Pressurage (rebêche)",
  vin_clair_transfert: "Transfert de vin clair",
  vin_clair_soutirage: "Soutirage",
  vin_clair_filtration: "Filtration",
  vin_clair_collage: "Collage",
  vin_clair_perte: "Perte de vin clair",
  vin_clair_assemblage_entrant: "Passage en assemblage",
  vin_clair_mise_en_reserve: "Mise en réserve",
  vin_clair_retour_reserve: "Retour de réserve",
  vin_clair_ajustement: "Ajustement de vin clair",
  vin_clair_annulation: "Annulation (vin clair)",
  reserve_apport: "Mise en réserve",
  reserve_sortie: "Sortie de réserve",
  bouteille_entree_tirage: "Tirage",
  bouteille_entree_reprise: "Reprise coopérative",
  bouteille_transfert: "Transfert de bouteilles",
  bouteille_degorgement: "Dégorgement",
  bouteille_habillage: "Habillage",
  bouteille_casse: "Casse",
  bouteille_temoin: "Bouteille témoin",
  bouteille_expedition: "Expédition",
  bouteille_ajustement: "Ajustement de bouteilles",
  bouteille_annulation: "Annulation (bouteilles)",
};

/** Libellé métier d'un `type_operation` (repli lisible « x_y » → « X y »). */
export function libelleTypeOperation(code: string): string {
  const connu = TYPE_OPERATION_LABELS[code];
  if (connu) return connu;
  const espace = code.replace(/_/g, " ").trim();
  return espace.charAt(0).toUpperCase() + espace.slice(1);
}

// ────────────────────────────────────────────────────────────────────────────
// Formats de nombres (mêmes conventions que les exports : virgule décimale,
// signe « − » U+2212, séparateur de milliers = espace insécable fine implicite).
// ────────────────────────────────────────────────────────────────────────────

/** Signe explicite d'affichage (« + » / « − » / rien pour 0). */
function signeDe(valeur: number): string {
  return valeur > 0 ? "+" : valeur < 0 ? "−" : "";
}

/** Groupe les milliers d'un entier positif par des espaces (« 3040 » → « 3 040 »). */
function grouperMilliers(entierPositif: number): string {
  return Math.round(entierPositif)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/**
 * Volume hL avec signe TOUJOURS affiché (« +45,600 » / « −18,750 » / « 0,000 »),
 * 3 décimales, sans groupement des milliers. À utiliser pour les DELTAS de
 * mouvement du journal registre (chaque ligne = une entrée/sortie signée).
 */
export function formatHlSigne(hl: number): string {
  return `${signeDe(hl)}${formatHlBilan(Math.abs(hl))}`;
}

/**
 * Volume hL du BILAN MATIÈRES : 3 décimales, « − » conservé mais jamais de « + »
 * sur les positifs, sans groupement des milliers. Ne pas confondre avec le
 * `formatHl` de `drm-ds-shared` (qui groupe les milliers pour les stocks DRM/DS).
 */
export function formatHlBilan(hl: number): string {
  return hl
    .toLocaleString("fr-FR", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 5,
      useGrouping: false,
    })
    .replace("-", "−");
}

/** Quantité de raisin en kg SIGNÉE (« +4 000 », « −3 000 »). */
export function formatKgSigne(kg: number): string {
  return `${signeDe(kg)}${grouperMilliers(Math.abs(kg))}`;
}

/** Quantité de raisin en kg — signe négatif conservé. */
export function formatKg(kg: number): string {
  return `${kg < 0 ? "−" : ""}${grouperMilliers(Math.abs(kg))}`;
}

/** Nombre de bouteilles SIGNÉ (« +3 040 », « −1 250 »). */
export function formatBouteillesSigne(nb: number): string {
  return `${signeDe(nb)}${grouperMilliers(Math.abs(nb))}`;
}

/** Nombre de bouteilles (non signé). */
export function formatBouteilles(nb: number): string {
  return grouperMilliers(Math.abs(nb));
}

/**
 * Montant € à partir de CENTIMES (droits d'accise prépayés CRD). Entrée en
 * centimes — à ne pas confondre avec `formatEuros` de `drm-ds-shared`, qui prend
 * déjà un montant en euros.
 */
export function formatEurosDepuisCents(cents: number): string {
  return (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

// ────────────────────────────────────────────────────────────────────────────
// Téléchargement d'un export (PDF / CSV)
// ────────────────────────────────────────────────────────────────────────────

/** Télécharge un export registre (PDF **ou** CSV) via le helper base64 partagé. */
export function telechargerExportRegistre(exportData: RegistreExportSortie): void {
  telechargerBase64(exportData.contenu_base64, exportData.mime, exportData.filename);
}

// ────────────────────────────────────────────────────────────────────────────
// Briques d'affichage partagées (hub + widget + registre)
// ────────────────────────────────────────────────────────────────────────────

/** Badge d'état d'une déclaration (icône verrou sur les états transmis). */
export function BadgeEtatDeclaration({ etat }: { etat: EtatDeclaration }) {
  const config = ETAT_DECLARATION_CONFIG[etat];
  return (
    <Badge variant={config.variant} size="sm">
      {config.verrouille && <Lock size={11} aria-hidden="true" />}
      {config.label}
    </Badge>
  );
}

/** Sens d'un mouvement : pastille colorée + mot (jamais la couleur seule). */
export function SensBadge({ sens }: { sens: SensRegistre }) {
  const config = SENS_CONFIG[sens];
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <StatusDot variant={config.dot} />
      {config.label}
    </span>
  );
}

// Message de chargement neutre : promu dans `@champ-erp/ui` (il est réimplémenté
// à l'identique dans une cinquantaine d'écrans). Ré-exporté ici pour ne pas
// casser les imports existants du module Conformité.
export { MessageChargement } from "@champ-erp/ui";
